import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {hooks} from '../hooks';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {actions} from '../store/actions';
import {TabScreen} from '../store/slices/tabSlice';
import axiosInstance from '../api/axiosInstance';

const tabs: {id: number; screen: TabScreen; icon: React.FC<{color?: string}>}[] = [
  {id: 1, screen: 'Home',         icon: svg.HomeTabSvg},
  {id: 2, screen: 'Transactions', icon: svg.TransactionsTabSvg},
  {id: 3, screen: 'Bracelet',     icon: svg.BraceletTabSvg},
  {id: 4, screen: 'Tips',         icon: svg.TipsTabSvg},
  {id: 5, screen: 'Profile',      icon: svg.ProfileTabSvg},
];

const unwrap = (r: any) => r.data?.data ?? r.data;

export const BottomTabBar: React.FC = () => {
  const dispatch = hooks.useAppDispatch();
  const {t} = useTranslation();
  const [hasPendingTips, setHasPendingTips] = useState(false);

  const currentTabScreen = hooks.useAppSelector(
    (state) => state.tabSlice.screen,
  );

  useEffect(() => {
    const checkPending = async () => {
      try {
        const data = await axiosInstance.get('/guest/tips/pending').then(unwrap);
        setHasPendingTips(Array.isArray(data) && data.length > 0);
      } catch {
        // badge just won't show
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 15_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        maxWidth: 650,
        margin: '0 auto',
        zIndex: 999,
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        paddingTop: 8,
        paddingInline: 8,
        backgroundColor: theme.colors.white,
        borderTop: `1px solid ${theme.colors.aliceBlue2}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          alignItems: 'center',
        }}
      >
        {tabs.map((tab) => {
          const isActive = currentTabScreen === tab.screen;
          return (
            <button
              key={tab.id}
              type='button'
              aria-label={t(`tabs.${tab.screen}`)}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => dispatch(actions.setScreen(tab.screen))}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '4px 12px',
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                minWidth: 52,
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <div style={{position: 'relative'}}>
                <tab.icon
                  color={isActive ? theme.colors.mainColor : '#AAAAAA'}
                />
                {tab.screen === 'Tips' && hasPendingTips && (
                  <span style={{
                    position: 'absolute',
                    top: -2,
                    insetInlineEnd: -3,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.colors.dangerRed,
                    border: '1.5px solid white',
                  }} />
                )}
              </div>

              <span
                style={{
                  fontSize: 10,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? theme.colors.mainColor : '#AAAAAA',
                  lineHeight: 1,
                  fontFamily: "'SS3', sans-serif",
                }}
              >
                {t(`tabs.${tab.screen}`)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
